import { createModel } from '@rematch/core';
import { createPath, generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import { AxiosError } from 'axios';

import { Location } from './locations';
import { MetaField } from './cycles';

const transformContractPayload = (payload) => {
  return {
    ...payload,
    locationId: payload.locationId || null,
  };
};

export type Contract = {
  id: string;
  name: string;
  locationId: string | null;
  endless: boolean;
  start_at: Date;
  end_at: Date | null;
  location: Location | null;
  default_meta_tags: MetaField[];
  isCelsius: boolean;
};

const DEFAULT_STATE: { contracts: Contract[]; contract: Contract | null } = {
  contracts: [],
  contract: null,
};

export const contracts = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    set(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    setOne(state, payload) {
      return {
        ...state,
        contract: payload,
      };
    },
    setList(state, payload) {
      return {
        ...state,
        contracts: payload,
      };
    },
    resetOne(state) {
      return {
        ...state,
        contract: DEFAULT_STATE.contract,
      };
    },
    resetList(state) {
      return {
        ...state,
        contracts: DEFAULT_STATE.contracts,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getContracts() {
        try {
          const response = await http(URL.CONTRACTS);

          dispatch.contracts.setList(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getContractsByLocation(data: { locationId: string; query?: { [key: string]: string } }) {
        try {
          const { locationId, query } = data;
          let path = generatePath(URL.CONTRACTS_LOCATION, { locationId });

          if (query) {
            const searchParams = new URLSearchParams(data.query).toString();
            path = createPath({ pathname: path, search: `?${searchParams}` });
          }

          const response = await http(path);

          dispatch.contracts.setList(response.data);
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async createContract(payload) {
        try {
          await http.post(URL.CONTRACTS, transformContractPayload(payload));
          toast.success('Contract created successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async deleteContract(id: string, state) {
        try {
          await http.delete(generatePath(URL.CONTRACT, { id }));

          const filteredList = state.contracts.contracts.filter((contract: Contract) => contract.id !== id);
          dispatch.contracts.setList(filteredList);
          toast.success('Contract deleted successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getContract(id: string) {
        try {
          const response = await http.get<Contract>(generatePath(URL.CONTRACT, { id }));

          if (response.data && response.data?.locationId) {
            await dispatch.locations.getCurrentLocation(response.data.locationId);
          }

          dispatch.contracts.setOne(response.data);
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateContract({ id, formData }) {
        try {
          const response = await http.put(generatePath(URL.CONTRACT, { id }), transformContractPayload(formData));
          dispatch.contracts.setOne(response.data);

          toast.success('Contract updated successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async addDefaultMetaTag(data: { id: string; metaField: MetaField }) {
        try {
          const response = await http.post(generatePath(URL.CONTRACT_DEFAULT_META_TAG, { id: data.id }), data);
          toast.success('Default meta tag added successfully');
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async removeDefaultMetaTag(data: { id: string; key: string }) {
        try {
          const path = generatePath(URL.CONTRACT_DEFAULT_META_TAG, { id: data.id });
          await http.delete(path, { data: { key: data.key } });
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateDefaultMetaTags({ id, updatedFields }: { id: string; updatedFields: MetaField[] }) {
        try {
          const path = generatePath(URL.UPDATE_DEFAULT_META_TAGS, { id });
          const response = await http.put(path, { updatedFields });

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
        }
      },
    };
  },
});
